import Button from '@rio-cloud/rio-uikit/Button';
import RadioButton from '@rio-cloud/rio-uikit/RadioButton';
import {
    type Control,
    Controller,
    type FieldErrors,
    type UseFormRegister,
    type UseFormSetValue,
    type UseFormTrigger,
    type UseFormWatch,
    useFieldArray,
} from 'react-hook-form';

import EInvoicingFields from '@/components/EInvoicingFields';
import type { InvoiceDataSchemaType } from '@/components/steps/InvoicingStep/invoiceDataSchema';
import Input from '@/components/ui/Input';
import SelectInput from '@/components/ui/SelectInput';
import { FeatureToggles } from '@/configuration/featureToggle/featureToggles';
import { useCountryNames } from '@/hooks/useCountryNames';
import { useFeatureToggle } from '@/hooks/useFeatureToggle';
import { getVatCountries, t } from '@/lib/utils';
import { getAllowedCountries } from '@/stores/countryStore';
import { useUserStore } from '@/stores/userStore';

type InvoicingSectionProps = {
    register: UseFormRegister<InvoiceDataSchemaType>;
    control: Control<InvoiceDataSchemaType>;
    watch: UseFormWatch<InvoiceDataSchemaType>;
    setValue: UseFormSetValue<InvoiceDataSchemaType>;
    trigger: UseFormTrigger<InvoiceDataSchemaType>;
    errors: FieldErrors<InvoiceDataSchemaType>;
};

const InvoicingSection = ({ register, control, errors, watch, setValue, trigger }: InvoicingSectionProps) => {
    const country = watch('invoicing.country');
    const additional = watch('invoicing.additional') ?? [];

    const countryNames = useCountryNames();
    const getCountryNamesForVat = useCountryNames;
    const { value: isAdditionVatEnabled } = useFeatureToggle(FeatureToggles.ENABLE_ADDITIONAL_VAT);
    const domesticVATCountry = countryNames?.find(item => item.id === country)?.label;

    const allowedCountries = getAllowedCountries();

    const { eInvoicingDetails } = useUserStore();
    const hasEInvoicing = eInvoicingDetails && eInvoicingDetails.length > 0;

    const { fields, append, remove } = useFieldArray({
        control,
        name: 'invoicing.additional',
    });

    return (
        <div className='margin-bottom-15 margin-top-5 rounded bg-white padding-25'>
            <div className='text-bold text-size-18 padding-bottom-15'>{t('onboarding.invoicingDetails.title')}</div>
            <div className='padding-bottom-15'>{t('onboarding.invoicingDetails.description')}</div>

            <Input
                required
                className='flex-1-1-0 margin-bottom-5'
                label={t('general.invoiceEmailAddress')}
                error={errors?.invoicing?.invoiceEmailAddress?.message}
                inputAttributes={{ ...register('invoicing.invoiceEmailAddress') }}
            />
            <div className='display-flex align-items-center margin-bottom-15'>
                <span className='uikit-icon icon-info-sign' />
                <i className='margin-left-5'>{t('general.beAwareThatAllInvoicesWillBeSent')}</i>
            </div>

            <div className='display-flex gap-15 padding-bottom-15'>
                <Input
                    className='flex-basis-33pct'
                    label={t('general.domesticVATCountry')}
                    inputAttributes={{
                        ...register('invoicing.country'),
                        value: domesticVATCountry,
                        disabled: true,
                    }}
                />
                <Input
                    className='flex-1-1-0'
                    label={t('general.domesticVATNumber')}
                    error={errors?.invoicing?.vat?.message}
                    inputAttributes={{
                        ...register('invoicing.vat'),
                    }}
                />
            </div>
            {hasEInvoicing && (
                <EInvoicingFields
                    control={control}
                    register={register}
                    watch={watch}
                    setValue={setValue}
                    trigger={trigger}
                    schemeIdName='invoicing.schemeId'
                    participantIdentifierName='invoicing.participantIdentifier'
                    vatName='invoicing.vat'
                    schemeIdError={errors.invoicing?.schemeId?.message}
                    participantIdentifierError={errors.invoicing?.participantIdentifier?.message}
                />
            )}

            {isAdditionVatEnabled && (
                <>
                    <hr />
                    <p>{t('onboarding.invoicingDetails.note')}</p>
                </>
            )}

            <div className='display-flex flex-column gap-25'>
                {fields.map((item, index) => (
                    <div key={item.id} className={index === fields.length - 1 ? 'margin-bottom-25' : ''}>
                        <div className='display-flex align-items-center gap-15'>
                            <Controller
                                name={`invoicing.additional.${index}.country`}
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                    <SelectInput
                                        required
                                        className='margin-bottom-0 min-width-200'
                                        error={errors.invoicing?.additional?.[index]?.country?.message}
                                        label={t('general.selectCountry')}
                                        selectAttributes={{
                                            onChange: selectItem => onChange(selectItem?.id),
                                            options: getCountryNamesForVat(
                                                value,
                                                getVatCountries(value ?? '', additional, country ?? '')
                                            ),
                                        }}
                                    />
                                )}
                            />
                            <Input
                                required
                                error={errors.invoicing?.additional?.[index]?.vat?.message}
                                inputAttributes={{
                                    ...register(`invoicing.additional.${index}.vat`),
                                }}
                                className='flex-1-1-0 margin-bottom-0'
                                label={t('general.VATNumber')}
                            />
                            <button
                                className='btn btn-icon-only uikit-icon icon-trash text-size-20 margin-top-20 width-auto text-color-dark hover-text-color-danger bg-white'
                                data-testid='delete-additional-button'
                                onClick={() => remove(index)}
                                type='button'
                            />
                        </div>

                        <p className='margin-bottom-5'>{t('general.doesTheCompanyHaveAPermanentEstablishment')}</p>
                        <div className='display-flex gap-15'>
                            <Controller
                                name={`invoicing.additional.${index}.permanentEstablishment`}
                                control={control}
                                render={({ field }) => (
                                    <RadioButton {...field} value='true' checked={field.value === 'true'}>
                                        {t('general.yes')}
                                    </RadioButton>
                                )}
                            />
                            <Controller
                                name={`invoicing.additional.${index}.permanentEstablishment`}
                                control={control}
                                render={({ field }) => (
                                    <RadioButton {...field} value='false' checked={field.value === 'false'}>
                                        {t('general.no')}
                                    </RadioButton>
                                )}
                            />
                        </div>
                    </div>
                ))}
            </div>

            {isAdditionVatEnabled && additional.length + 1 < allowedCountries.length && (
                <Button
                    onClick={() =>
                        append(
                            {
                                vat: '',
                                country: '',
                                permanentEstablishment: 'true',
                            },
                            { shouldFocus: false }
                        )
                    }
                >
                    <span>{t('general.button.addAnotherVAT')}</span>
                </Button>
            )}
        </div>
    );
};

export default InvoicingSection;
